import { en } from './en';
import { pl } from './pl';

export { localize } from './localize';

export type Locale = 'en' | 'pl';

const locales: Record<Locale, Record<string, string>> = { en, pl };

export function getLocale(): Locale {
  const stored = localStorage.getItem('locale');
  if (stored === 'en' || stored === 'pl') return stored;
  return 'en';
}

export function setLocale(locale: Locale): void {
  localStorage.setItem('locale', locale);
  window.dispatchEvent(new Event('locale-changed'));
}

/**
 * Translate a key with optional positional interpolation.
 * Placeholders `{0}`, `{1}`, etc. are replaced with corresponding args.
 * Returns the key itself if missing (fallback).
 */
export function t(key: string, ...args: (string | number)[]): string {
  const locale = getLocale();
  let str = locales[locale][key];
  if (str === undefined) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      console.warn(`[i18n] Missing key: '${key}' for locale '${locale}'`);
    }
    return key;
  }
  for (let i = 0; i < args.length; i++) {
    str = str.replace(new RegExp(`\\{${i}\\}`, 'g'), String(args[i]));
  }
  return str;
}

function getPluralForm(count: number, locale: Locale): string {
  if (locale === 'pl') {
    if (count === 1) return 'one';
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few';
    return 'many';
  }
  return count === 1 ? 'one' : 'other';
}

/**
 * Plural-aware translation. Selects the correct plural form based on count
 * and active locale. Keys use `plural.<key>.<form>` pattern.
 */
export function tp(count: number, key: string): string {
  const locale = getLocale();
  const form = getPluralForm(count, locale);
  const fullKey = `plural.${key}.${form}`;
  let str = locales[locale][fullKey];
  if (str === undefined) {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      console.warn(`[i18n] Missing plural key: '${fullKey}' for locale '${locale}'`);
    }
    return `${count} ${key}`;
  }
  str = str.replace(/\{0\}/g, String(count));
  return str;
}
